import { supabase } from '../lib/supabase'
import type { Database } from '../lib/supabase'
import { format } from 'date-fns'

type Unavailability = Database['public']['Tables']['subcontractor_unavailability']['Row']
type UnavailabilityInsert = Database['public']['Tables']['subcontractor_unavailability']['Insert']

export type { Unavailability, UnavailabilityInsert }

export const unavailabilityService = {
  // Hämta alla undantag för en trädgårdsmästare
  async getBySubcontractor(subcontractorId: string): Promise<Unavailability[]> {
    const { data, error } = await supabase
      .from('subcontractor_unavailability')
      .select('*')
      .eq('subcontractor_id', subcontractorId)
      .order('start_date', { ascending: true })

    if (error) {
      console.error('Error fetching unavailability:', error)
      throw error
    }

    return data || []
  },

  // Hämta undantag för flera trädgårdsmästare (för Planning-vyn)
  async getBySubcontractorIds(ids: string[]): Promise<Unavailability[]> {
    if (ids.length === 0) return []

    const { data, error } = await supabase
      .from('subcontractor_unavailability')
      .select('*')
      .in('subcontractor_id', ids)
      .order('start_date', { ascending: true })

    if (error) {
      console.error('Error fetching unavailability for multiple subcontractors:', error)
      throw error
    }

    return data || []
  },

  // Skapa nytt undantag
  async create(unavailability: UnavailabilityInsert): Promise<Unavailability> {
    const { data, error } = await supabase
      .from('subcontractor_unavailability')
      .insert([unavailability])
      .select()
      .single()

    if (error) {
      console.error('Error creating unavailability:', error)
      throw error
    }

    return data
  },

  // Ta bort undantag
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('subcontractor_unavailability')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting unavailability:', error)
      throw error
    }
  }
}

// Hjälpfunktion: Kolla om ett datum faller inom ett undantagsperiod
export function isDateInUnavailabilityPeriod(
  subcontractorId: string,
  date: Date,
  unavailabilityData: Unavailability[]
): { unavailable: boolean; reason?: string } {
  // Använd format() istället för toISOString() för att respektera lokal tidszon
  // toISOString() konverterar till UTC vilket kan skifta datumet bakåt en dag
  const dateStr = format(date, 'yyyy-MM-dd')

  const match = unavailabilityData.find(u =>
    u.subcontractor_id === subcontractorId &&
    dateStr >= u.start_date &&
    dateStr <= u.end_date
  )

  if (match) {
    return { unavailable: true, reason: match.reason || undefined }
  }

  return { unavailable: false }
}
